/* The chart an answer's own table can support.
 *
 * It plots nothing the table does not already state, so it cannot disagree with the answer
 * or with the citation behind it — the table stays on screen beside it as the source of
 * truth and the accessible view of the same numbers.
 *
 * Palette: #5F8F26 (the KBL green already in the tokens), #2E6FA2, #A8541E — checked with
 * the six-check validator (lightness band, chroma floor, CVD separation on every adjacent
 * pair, normal-vision floor, contrast against the surface) rather than chosen by eye.
 */

import { useEffect, useRef } from 'react'
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  Tooltip,
  type ChartOptions,
} from 'chart.js'

import type { ChartableTable } from './answerFormat'

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

const SERIES_COLORS = ['#5F8F26', '#2E6FA2', '#A8541E']
const INK = '#1F2C3B'
const MUTED = '#8A959F'
const GRID = '#EDF0F2'

/** Long labels or many rows read better lying down. */
function isHorizontal(data: ChartableTable): boolean {
  return data.labels.length > 6 || data.labels.some((l) => l.length > 14)
}

/** Direct labels are drawn from the table's own strings, never re-formatted arithmetic. */
function directLabels(data: ChartableTable, horizontal: boolean) {
  return {
    id: 'kblDirectLabels',
    afterDatasetsDraw(chart: Chart) {
      // Selective, per the mark spec: a number on every mark of a dense chart is noise.
      if (data.labels.length > 8 || data.series.length > 2) return
      const { ctx } = chart
      ctx.save()
      ctx.font = "600 11px 'Poppins', sans-serif"
      ctx.fillStyle = INK
      chart.data.datasets.forEach((_, si) => {
        const meta = chart.getDatasetMeta(si)
        if (meta.hidden) return
        meta.data.forEach((element, ri) => {
          const text = data.display[ri]?.[si] ?? ''
          if (!text) return
          const { x, y } = element.getProps(['x', 'y'], true)
          if (horizontal) {
            ctx.textAlign = 'left'
            ctx.textBaseline = 'middle'
            ctx.fillText(text, x + 8, y)
          } else {
            ctx.textAlign = 'center'
            ctx.textBaseline = 'bottom'
            ctx.fillText(text, x, y - 6)
          }
        })
      })
      ctx.restore()
    },
  }
}

export function AnswerChart({ data, caption }: { data: ChartableTable; caption?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const horizontal = isHorizontal(data)
    const multi = data.series.length > 1

    const options: ChartOptions<'bar'> = {
      indexAxis: horizontal ? 'y' : 'x',
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 14, right: horizontal ? 56 : 12, bottom: 2, left: 2 } },
      plugins: {
        // One series is named by the answer around it; a legend box would repeat it.
        legend: multi
          ? {
              display: true,
              position: 'top',
              align: 'start',
              labels: {
                boxWidth: 10,
                boxHeight: 10,
                borderRadius: 3,
                useBorderRadius: true,
                // Text wears text tokens; the swatch beside it carries the identity.
                color: '#5A6774',
                font: { size: 11.5 },
                padding: 14,
              },
            }
          : { display: false },
        tooltip: {
          backgroundColor: INK,
          titleFont: { size: 12, weight: 600 },
          bodyFont: { size: 12 },
          padding: 10,
          cornerRadius: 8,
          displayColors: multi,
          callbacks: {
            label: (item) => {
              const shown = data.display[item.dataIndex]?.[item.datasetIndex] ?? ''
              const name = data.series[item.datasetIndex]?.name ?? ''
              return multi ? ` ${name}: ${shown}` : ` ${shown}`
            },
          },
        },
      },
      scales: {
        x: {
          // Gridlines belong to the value axis only — on the category axis they fence the
          // labels without helping anyone read a magnitude.
          grid: { display: horizontal, color: GRID, drawTicks: false },
          border: { display: false },
          ticks: { color: MUTED, font: { size: 11 }, maxRotation: 0, autoSkip: true },
          beginAtZero: true,
        },
        y: {
          grid: { display: !horizontal, color: GRID, drawTicks: false },
          border: { display: false },
          ticks: { color: MUTED, font: { size: 11 } },
          beginAtZero: true,
        },
      },
    }

    chartRef.current = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: data.labels,
        datasets: data.series.map((s, i) => ({
          label: s.name,
          data: s.values,
          backgroundColor: SERIES_COLORS[i % SERIES_COLORS.length],
          // Rounded data-end only: the end anchored to the baseline stays square.
          borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: horizontal ? 'left' : 'bottom',
          // A 2px-ish gap of surface between adjacent bars. A two-bar comparison gets a
          // wider mark — at 46px on a full-width card it read as a pair of pins.
          categoryPercentage: 0.72,
          barPercentage: 0.86,
          maxBarThickness: data.labels.length <= 3 ? 76 : 46,
        })),
      },
      options,
      plugins: [directLabels(data, horizontal)],
    })

    return () => {
      chartRef.current?.destroy()
      chartRef.current = null
    }
  }, [data])

  const height = isHorizontal(data) ? Math.max(150, data.labels.length * 34 + 40) : 230
  // The unit belongs on the chart once, not on every bar.
  const note = caption ?? (data.unitLabel ? `Figures in ${data.unitLabel}` : undefined)

  return (
    <figure className="ans-chart">
      <div className="ans-chart-canvas" style={{ height }}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`Chart of ${data.series.map((s) => s.name).join(' and ')} by ${
            data.labels.join(', ')
          }. The same figures are in the table view.`}
        />
      </div>
      {note && <figcaption>{note}</figcaption>}
    </figure>
  )
}
